/**
 * Demo page for AnalysisDiff component
 * Access at: /demo/analysis-diff
 */

import React, { useState } from 'react';
import { IonButton } from '@ionic/react';
import { AnalysisDiff } from '../../components/shared/AnalysisDiff';
import './AnalysisDiffDemo.css';

// Sample ticket data (what you'd see in Jira)
const SAMPLE_TICKET = {
  key: 'SCRUM-142',
  summary: 'Add dark mode toggle',
  description: 'We need dark mode in the app. Users have been asking for it.',
  labels: [],
  type: 'Story',
  priority: 'Medium',
  status: 'To Do'
};

// Sample suggested changes from AI analysis
const SAMPLE_SUGGESTED_CHANGES = [
  {
    id: 'change-1-summary',
    field: 'summary',
    original: SAMPLE_TICKET.summary,
    suggested: '[Feature] Add dark mode toggle to Settings page with system preference detection',
    reasoning: 'The improved summary clarifies this is a feature request, specifies where the toggle should be located, and mentions the system preference detection which is a common UX expectation for dark mode implementations.',
    status: 'pending'
  },
  {
    id: 'change-2-description',
    field: 'description',
    original: SAMPLE_TICKET.description,
    suggested: `## Overview
Implement a dark mode theme toggle that allows users to switch between light and dark themes throughout the application.

## User Story
As a user, I want to toggle between light and dark mode so that I can use the app comfortably in different lighting conditions.

## Requirements
- Add a toggle switch in the Settings page under "Appearance"
- Detect system preference on first visit (prefers-color-scheme)
- Persist user preference in localStorage
- Apply theme changes instantly without page reload

## Technical Notes
- Use CSS custom properties (variables) for theme colors
- Consider using a ThemeContext for React state management
- Ensure all components respect the theme variables

## Acceptance Criteria
- [ ] Dark mode toggle is visible in Settings > Appearance
- [ ] Theme changes apply immediately to all pages
- [ ] User preference persists across sessions
- [ ] System preference is respected on first visit
- [ ] All text remains readable in both themes
- [ ] Images and icons have appropriate contrast`,
    reasoning: 'The original description lacks detail for implementation. Added user story format, clear requirements, technical guidance, and testable acceptance criteria to make the ticket actionable.',
    status: 'pending'
  },
  {
    id: 'change-3-labels',
    field: 'labels',
    original: 'None',
    suggested: 'feature, frontend, ux, settings, accessibility',
    reasoning: 'Added labels to categorize this as a frontend feature affecting UX and accessibility, located in the settings area. This helps with sprint planning and filtering.',
    status: 'pending'
  }
];

const AnalysisDiffDemo = () => {
  const [changes, setChanges] = useState(SAMPLE_SUGGESTED_CHANGES);
  const [applied, setApplied] = useState(false);
  const [appliedResult, setAppliedResult] = useState(null);

  const handleApplyComplete = (result) => {
    console.log('Apply complete:', result);
    setApplied(true);
    setAppliedResult(result);
  };

  const handleReset = () => {
    setChanges(SAMPLE_SUGGESTED_CHANGES.map(c => ({ ...c, status: 'pending' })));
    setApplied(false);
    setAppliedResult(null);
  };

  return (
    <div className="analysis-diff-demo-page">
      <div className="demo-header">
        <h1>AnalysisDiff Component Demo</h1>
        <p className="demo-subtitle">
          Test the AI-suggested changes diff UI component
        </p>
      </div>

      {/* Original Ticket Display */}
      <div className="demo-ticket-preview">
        <h4>Original Ticket: {SAMPLE_TICKET.key}</h4>
        <div className="ticket-meta">
          <span className="ticket-type">{SAMPLE_TICKET.type}</span>
          <span className="ticket-priority">{SAMPLE_TICKET.priority}</span>
          <span className="ticket-status">{SAMPLE_TICKET.status}</span>
        </div>
        <div className="ticket-field">
          <label>Summary:</label>
          <p>{SAMPLE_TICKET.summary}</p>
        </div>
        <div className="ticket-field">
          <label>Description:</label>
          <p>{SAMPLE_TICKET.description}</p>
        </div>
        <div className="ticket-field">
          <label>Labels:</label>
          <p>{SAMPLE_TICKET.labels.length > 0 ? SAMPLE_TICKET.labels.join(', ') : 'None'}</p>
        </div>
      </div>

      <div className="demo-content">
        {!applied ? (
          <AnalysisDiff
            suggestedChanges={changes}
            analysisId="demo-analysis-123"
            projectId="demo-project"
            ticketKey={SAMPLE_TICKET.key}
            onApplyComplete={handleApplyComplete}
          />
        ) : (
          <div className="demo-applied-state">
            <div className="applied-success">
              <h3>✅ Changes Applied Successfully</h3>
              <p>The following fields were updated:</p>
              <pre>{JSON.stringify(appliedResult, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>

      <div className="demo-controls">
        <h4>Demo Controls</h4>
        <IonButton onClick={handleReset} fill="outline">
          Reset Demo
        </IonButton>
        <IonButton 
          onClick={() => setChanges(changes.map(c => ({ ...c, status: 'accepted' })))}
          fill="outline"
          color="success"
        >
          Accept All (Preview)
        </IonButton>
        <IonButton 
          onClick={() => setChanges(changes.map(c => ({ ...c, status: 'rejected' })))}
          fill="outline"
          color="medium"
        >
          Reject All (Preview)
        </IonButton>
      </div>

      <div className="demo-info">
        <h4>Component Props</h4>
        <table>
          <thead>
            <tr>
              <th>Prop</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>suggestedChanges</code></td>
              <td>Array</td>
              <td>Array of change objects with id, field, original, suggested, reasoning, status</td>
            </tr>
            <tr>
              <td><code>analysisId</code></td>
              <td>String</td>
              <td>ID of the analysis execution</td>
            </tr>
            <tr>
              <td><code>projectId</code></td>
              <td>String</td>
              <td>Project ID for API calls</td>
            </tr>
            <tr>
              <td><code>ticketKey</code></td>
              <td>String</td>
              <td>Jira ticket key (e.g., PROJ-123)</td>
            </tr>
            <tr>
              <td><code>onApplyComplete</code></td>
              <td>Function</td>
              <td>Callback when changes are applied</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnalysisDiffDemo;
